// The Tasks page (2026-10-12, Lab-only behind TASKS_ENABLED): a tracker for
// work someone has to handle at a later date, replacing Support's Azure
// DevOps board and its daily scrub email. Modeled on the Parked page: one
// table of every task, every creator's; a New task button; filters remembered
// per browser; details editable in place with the change log beneath.
// Admins, and csrs with Support Permissions / Support Email / Support Voice;
// the server enforces it on every route.
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, IconButton, InputAdornment, Switch,
  InputLabel, Link, ListItemText, MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Typography,
} from '@mui/material'
import { TASK_TYPES, TASK_CREATED_PRESETS, TASK_ACTION_PRESETS, TASK_STATUS_LABELS, taskType, taskTypeLabel, validateTaskInput, isTaskDue } from '@cxhub/shared'
import { fetchTasks, fetchTask, createTask, updateTask, closeTask as closeTaskApi } from './api.js'
import DraggableModal from './DraggableModal.jsx'
import {
  EMPTY_TASK_FILTERS, DEFAULT_TASK_SORT, hasTaskFilters, readTaskFilters, writeTaskFilters, taskListQuery, taskSortDirection, nextTaskSort,
  formatTaskTimestamp, formatTaskActionNeededBy, orderDetailsSegments, taskEventText,
  isFifthBusinessDayChecked, isFromLastScanChecked, isTrackingTriggerChecked, isReturnDocumentTriggerChecked, hasDueTrigger, setReturnDocumentTrigger, setFifthBusinessDay, setFromLastScan, setLastScanDate, setTrackingTrigger, setActionNeededByDate,
  clearActionNeededByDate, clearActionNeededByTime, clearLastScanDate,
  FIFTH_BUSINESS_DAY_LABEL, FIFTH_BUSINESS_DAY_FROM_LAST_SCAN_LABEL, TRACKING_TRIGGER_LABEL, RETURN_DOCUMENT_TRIGGER_LABEL, todayEastern,
} from './tasks.js'

// Order Details as entered; each reference the server resolved is a link to
// the same destination as the contact header's order number.
export function OrderDetailsText({ text, orderLinks }) {
  return (
    <span>
      {orderDetailsSegments(text, orderLinks).map((s, i) => (s.href
        ? <Link key={i} href={s.href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} data-testid="task-order-link">{s.text}</Link>
        : <span key={i}>{s.text}</span>))}
    </span>
  )
}

// A native date or time input draws in the browser's own font; it takes the
// theme's, like every other field (2026-10-13).
const NATIVE_INPUT_SX = { '& input': { font: 'inherit' } }

// The clear X inside a date or time field (2026-10-14), shown only when the
// field has a value.
function clearAdornment(value, label, testId, onClear, disabled) {
  if (!value || disabled) return undefined
  return (
    <InputAdornment position="end">
      <IconButton size="small" edge="end" aria-label={`Clear ${label}`} onClick={onClear} data-testid={testId}>
        <XIcon />
      </IconButton>
    </InputAdornment>
  )
}

// A type's fields, rendered from the shared definition. values are API-keyed
// (actionNeededByDate / actionNeededByTime for a datetime field). A datetime
// field with a section (2026-10-13) sits under its own heading, with three
// optional, mutually exclusive options above the date, stacked one per line
// (2026-10-14) in this order: "Replacement Order Tracking is Available"
// stands in for the date (the date and time are cleared and hidden); "5th
// Business Day" fills it; "5th Business Day From Last Scan" fills it from a
// required Last Scan Date shown directly under it (not after today, Eastern);
// "Return Document Created" (2026-10-14) stands in for the date too.
// Changing or clearing the date by hand unchecks the two 5th Business Day
// options. The date, time and Last Scan Date each have a clear X.
export function TaskFieldInputs({ type, values, onChange, errors = {}, disabled = false }) {
  const def = taskType(type)
  if (!def) return null
  const set = (key, v) => onChange({ ...values, [key]: v })
  return (
    <Stack spacing={1.5}>
      {def.fields.map((f) => {
        if (f.kind === 'datetime') {
          const dk = `${f.key}Date`
          const tk = `${f.key}Time`
          const hasTriggers = Boolean(f.triggers?.length)
          const tracking = hasTriggers && isTrackingTriggerChecked(values)
          const triggered = hasTriggers && hasDueTrigger(values)
          return (
            <Box key={f.key} data-testid={`task-section-${f.key}`}>
              {f.section && (
                <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }} data-testid="task-section-heading">{f.section}</Typography>
              )}
              {hasTriggers && (
                <Stack direction="column" spacing={0} sx={{ mb: 1 }} data-testid="task-action-options">
                  <FormControlLabel
                    label={TRACKING_TRIGGER_LABEL} disabled={disabled}
                    control={<Checkbox size="small" checked={tracking} onChange={(e) => onChange(setTrackingTrigger(values, e.target.checked))} inputProps={{ 'data-testid': 'task-tracking-trigger' }} />}
                  />
                  <FormControlLabel
                    label={FIFTH_BUSINESS_DAY_LABEL} disabled={disabled}
                    control={<Checkbox size="small" checked={isFifthBusinessDayChecked(values)} onChange={(e) => onChange(setFifthBusinessDay(values, e.target.checked))} inputProps={{ 'data-testid': 'task-fifth-business-day' }} />}
                  />
                  {/* Its Last Scan Date sits on the same line, right of the
                      label (2026-10-15), wrapping below only when narrow. */}
                  <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap columnGap={1} data-testid="task-from-last-scan-row">
                    <FormControlLabel
                      label={FIFTH_BUSINESS_DAY_FROM_LAST_SCAN_LABEL} disabled={disabled}
                      control={<Checkbox size="small" checked={isFromLastScanChecked(values)} onChange={(e) => onChange(setFromLastScan(values, e.target.checked))} inputProps={{ 'data-testid': 'task-from-last-scan' }} />}
                    />
                    {isFromLastScanChecked(values) && (() => {
                      const today = todayEastern()
                      const future = Boolean(values.lastScanDate) && values.lastScanDate > today
                      const message = errors.lastScanDate ?? (future ? "Last Scan Date can't be in the future." : null)
                      return (
                        <TextField
                          type="date" size="small" label="Last Scan Date" required value={values.lastScanDate ?? ''} disabled={disabled}
                          onChange={(e) => onChange(setLastScanDate(values, e.target.value))} InputLabelProps={{ shrink: true }} sx={{ ...NATIVE_INPUT_SX, my: 0.5, maxWidth: 220 }}
                          error={Boolean(message)} helperText={message ?? 'Date only'} inputProps={{ max: today, 'data-testid': 'task-field-lastScanDate' }}
                          InputProps={{ endAdornment: clearAdornment(values.lastScanDate, 'Last Scan Date', 'task-clear-lastScanDate', () => onChange(clearLastScanDate(values)), disabled) }}
                        />
                      )
                    })()}
                  </Stack>
                  <FormControlLabel
                    label={RETURN_DOCUMENT_TRIGGER_LABEL} disabled={disabled}
                    control={<Checkbox size="small" checked={isReturnDocumentTriggerChecked(values)} onChange={(e) => onChange(setReturnDocumentTrigger(values, e.target.checked))} inputProps={{ 'data-testid': 'task-return-document-trigger' }} />}
                  />
                </Stack>
              )}
              {(errors.dueTrigger || errors.dueBasis) && <Typography variant="caption" color="error" display="block" sx={{ mb: 1 }}>{errors.dueTrigger ?? errors.dueBasis}</Typography>}
              {!triggered && (
                <Stack direction="row" spacing={1}>
                  <TextField
                    type="date" size="small" label={f.label} required={f.required} value={values[dk] ?? ''} disabled={disabled}
                    onChange={(e) => onChange(setActionNeededByDate(values, e.target.value))} InputLabelProps={{ shrink: true }} sx={NATIVE_INPUT_SX}
                    error={Boolean(errors[dk])} helperText={errors[dk] ?? ' '} inputProps={{ 'data-testid': `task-field-${dk}` }}
                    InputProps={{ endAdornment: clearAdornment(values[dk], f.label, `task-clear-${dk}`, () => onChange(clearActionNeededByDate(values)), disabled) }}
                  />
                  <TextField
                    type="time" size="small" label="Time (optional)" value={values[tk] ?? ''} disabled={disabled || !values[dk]}
                    onChange={(e) => set(tk, e.target.value)} InputLabelProps={{ shrink: true }} sx={NATIVE_INPUT_SX}
                    error={Boolean(errors[tk])} helperText={errors[tk] ?? 'Eastern time'} inputProps={{ 'data-testid': `task-field-${tk}` }}
                    InputProps={{ endAdornment: clearAdornment(values[tk], 'Time', `task-clear-${tk}`, () => onChange(clearActionNeededByTime(values)), disabled || !values[dk]) }}
                  />
                </Stack>
              )}
            </Box>
          )
        }
        return (
          <TextField
            key={f.key} size="small" label={f.label} required={f.required} value={values[f.key] ?? ''} disabled={disabled}
            multiline={f.kind === 'multiline'} minRows={f.kind === 'multiline' ? TASK_DESCRIPTION_MIN_ROWS : undefined} fullWidth
            onChange={(e) => set(f.key, e.target.value)} error={Boolean(errors[f.key])} helperText={errors[f.key] ?? ' '}
            inputProps={{ 'data-testid': `task-field-${f.key}` }}
          />
        )
      })}
    </Stack>
  )
}

// The task a /tasks?task=<id> link opens, or null.
function taskIdFromLocation() {
  try {
    const raw = new URLSearchParams(window.location.search).get('task')
    const id = raw == null ? NaN : Number(String(raw).replace(/^t/i, ''))
    return Number.isInteger(id) && id > 0 ? id : null
  } catch {
    return null
  }
}

// The Description box (2026-10-15: two rows taller than the first 3).
export const TASK_DESCRIPTION_MIN_ROWS = 5

// Save is enabled only when the shared definition's required fields are
// filled and nothing is invalid.
export function taskFormReady(type, values) {
  return Boolean(taskType(type)) && Object.keys(validateTaskInput(type, values).errors).length === 0
}

// The one create-task form (2026-10-15): the Tasks page's "New task" and a
// Support contact's Other Actions "Create Task". A movable, non-blocking
// window (DraggableModal: no backdrop, no focus trap, Escape and X close it,
// centered each time it opens), so the agent can copy from the contact
// behind it. initialValues prefill the form (a contact's order number);
// sourceConversationId records the contact it was created from.
export function CreateTaskDialog({ open, onClose, onCreated, initialValues = null, sourceConversationId = null }) {
  const [type, setType] = useState(TASK_TYPES[0].key)
  const [values, setValues] = useState({})
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  useEffect(() => { if (open) { setValues({ ...(initialValues ?? {}) }); setErrors({}); setError(null) } }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setSaving(true); setError(null)
    try {
      const { task } = await createTask({ type, ...values, ...(sourceConversationId ? { sourceConversationId } : {}) })
      onCreated?.(task)
    } catch (err) {
      if (err.errors) setErrors(err.errors)
      else setError('Could not save the task. Try again.')
    } finally {
      setSaving(false)
    }
  }
  return (
    <DraggableModal
      open={open} onClose={onClose} title="New task" testId="task-create-dialog" centered
      width={Math.min(600, typeof window === 'undefined' ? 600 : window.innerWidth - 32)}
      height={Math.min(720, typeof window === 'undefined' ? 720 : window.innerHeight - 32)}
      minWidth={320} minHeight={320}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !taskFormReady(type, values)} data-testid="task-create-save">Save</Button>
        </>
      )}
    >
      <Stack spacing={1.5} sx={{ pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <FormControl size="small">
          <InputLabel id="task-type-label">Type</InputLabel>
          <Select labelId="task-type-label" label="Type" value={type} onChange={(e) => { setType(e.target.value); setValues({ ...(initialValues ?? {}) }) }} SelectDisplayProps={{ 'data-testid': 'task-type' }}>
            {TASK_TYPES.map((t) => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TaskFieldInputs type={type} values={values} onChange={setValues} errors={errors} disabled={saving} />
      </Stack>
    </DraggableModal>
  )
}

// "Task T1001 created" after a save (2026-10-15).
export function TaskCreatedSnackbar({ task, onClose }) {
  return (
    <Snackbar open={Boolean(task)} autoHideDuration={6000} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert severity="success" onClose={onClose} data-testid="task-created-message">{task ? `Task ${task.displayId ?? `T${task.id}`} created` : ''}</Alert>
    </Snackbar>
  )
}

const EDIT_KEYS = ['orderDetails', 'description', 'actionNeededByDate', 'actionNeededByTime', 'dueBasis', 'lastScanDate', 'dueTrigger']

// One task's live fields, change log, edit and close (2026-10-13: shared by
// the Tasks page's details dialog and a task contact's Task card).
function useTaskDetails(taskId, { onSaved } = {}) {
  const [data, setData] = useState(null)
  const [editing, setEditing] = useState(false)
  const [values, setValues] = useState({})
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const load = useCallback(() => {
    if (taskId == null) return
    fetchTask(taskId).then(setData).catch(() => setError('Could not load the task.'))
  }, [taskId])
  useEffect(() => { setData(null); setEditing(false); setErrors({}); setError(null); load() }, [load])
  const task = data?.task ?? null
  const startEdit = () => {
    setValues(Object.fromEntries(EDIT_KEYS.map((k) => [k, task[k] ?? ''])))
    setErrors({})
    setEditing(true)
  }
  const save = async () => {
    setSaving(true); setError(null)
    try {
      const res = await updateTask(task.id, values)
      setData({ task: res.task, events: res.events, orderLinks: res.orderLinks })
      setEditing(false)
      onSaved?.()
    } catch (err) {
      if (err.errors) setErrors(err.errors)
      else setError('Could not save the task. Try again.')
    } finally {
      setSaving(false)
    }
  }
  const close = async (note) => {
    setSaving(true); setError(null)
    try {
      const res = await closeTaskApi(task.id, note)
      setData({ task: res.task, events: res.events, orderLinks: res.orderLinks })
      onSaved?.()
      return true
    } catch (err) {
      setError(err.errors?.note ?? 'Could not close the task. Try again.')
      return false
    } finally {
      setSaving(false)
    }
  }
  return { data, task, editing, setEditing, values, setValues, errors, saving, error, startEdit, save, close }
}

// The task's fields (or its edit form) and its change log, newest first.
function TaskDetailsBody({ details, onOpenContact = null }) {
  const { data, task, editing, values, setValues, errors, saving } = details
  if (!task) return null
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary" data-testid="task-created-line">
        Created by {task.createdByName ?? 'unknown'} on {formatTaskTimestamp(task.createdAt)} · <span data-testid="task-status">{TASK_STATUS_LABELS[task.status] ?? task.status}</span>
      </Typography>
      {/* Created from a Support contact (2026-10-15). */}
      {task.sourceConversationId && (
        <Typography variant="body2" data-testid="task-source-contact">
          Created from contact{' '}
          {onOpenContact
            ? <Link component="button" type="button" variant="body2" onClick={() => onOpenContact(task.sourceConversationId)} data-testid="task-source-contact-link">{task.sourceConvNumber ?? 'contact'}</Link>
            : <span>{task.sourceConvNumber ?? ''}</span>}
        </Typography>
      )}
      {editing ? (
        <TaskFieldInputs type={task.type} values={values} onChange={setValues} errors={errors} disabled={saving} />
      ) : (
        <Stack spacing={1}>
          <Field label="Order Number"><OrderDetailsText text={task.orderDetails} orderLinks={data.orderLinks} /></Field>
          <Field label="Description"><span style={{ whiteSpace: 'pre-wrap' }} data-testid="task-description">{task.description}</span></Field>
          <Field label="Action Needed By"><span data-testid="task-action-needed-by-value">{formatTaskActionNeededBy(task) || '—'}</span></Field>
          {task.status === 'closed' && (
            <Field label="Closed">{formatTaskTimestamp(task.closedAt)} · {task.closedByName ?? 'unknown'} · <span data-testid="task-close-note-text">{task.closeNote}</span></Field>
          )}
        </Stack>
      )}
      <Divider />
      <Typography variant="subtitle2">Change log</Typography>
      <Stack spacing={0.5} data-testid="task-log">
        {(data.events ?? []).map((e) => (
          <Typography key={e.id} variant="body2" data-testid="task-log-entry">
            <strong>{formatTaskTimestamp(e.changedAt)}</strong> · {e.changedByName ?? 'unknown'} · {taskEventText(e)}
          </Typography>
        ))}
      </Stack>
    </Stack>
  )
}

// "Mark Task Completed" (2026-10-13, renamed from "Close task" 2026-10-14):
// a required note, for any open task; the task and its contact close.
function CloseTaskDialog({ open, onClose, onConfirm, saving }) {
  const [note, setNote] = useState('')
  useEffect(() => { if (open) setNote('') }, [open])
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" data-testid="task-close-dialog">
      <DialogTitle>Mark Task Completed</DialogTitle>
      <DialogContent>
        <TextField
          label="Close note (required)" multiline minRows={3} fullWidth size="small" sx={{ mt: 1 }}
          value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ 'data-testid': 'task-close-dialog-note' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={() => onConfirm(note.trim())} disabled={saving || !note.trim()} data-testid="task-close-dialog-confirm">Mark Task Completed</Button>
      </DialogActions>
    </Dialog>
  )
}

function XIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

function TaskDetailsDialog({ taskId, onClose, onSaved, onOpenContact = null }) {
  const details = useTaskDetails(taskId, { onSaved })
  const { task, editing, setEditing, saving, error, startEdit, save, close, values } = details
  const [closing, setClosing] = useState(false)
  return (
    <Dialog open={taskId != null} onClose={onClose} fullWidth maxWidth="md" data-testid="task-details">
      {/* Closed with the X in the top-right corner (2026-10-14), as other
          CXHub windows are; there is no bottom Close button. */}
      <DialogTitle sx={{ pr: 6 }}>{task ? `${task.displayId} · ${taskTypeLabel(task.type)}` : 'Task'}</DialogTitle>
      <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ position: 'absolute', right: 12, top: 12 }} data-testid="task-details-x">
        <XIcon />
      </IconButton>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {task?.conversationId && onOpenContact && (
          <Link component="button" type="button" variant="body2" sx={{ mb: 1 }} onClick={() => onOpenContact(task.conversationId)} data-testid="task-contact-link">Open its contact</Link>
        )}
        <TaskDetailsBody details={details} onOpenContact={onOpenContact} />
      </DialogContent>
      <DialogActions>
        {editing ? (
          <>
            <Button onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={save} disabled={saving || !taskFormReady(task.type, values)} data-testid="task-edit-save">Save</Button>
          </>
        ) : (
          <>
            {task && task.status !== 'closed' && (
              <Button color="inherit" variant="outlined" onClick={() => setClosing(true)} data-testid="task-close">Mark Task Completed</Button>
            )}
            <Button variant="contained" onClick={startEdit} disabled={!task || task.status === 'closed'} data-testid="task-edit">Edit</Button>
          </>
        )}
      </DialogActions>
      <CloseTaskDialog open={closing} saving={saving} onClose={() => setClosing(false)} onConfirm={async (note) => { if (await close(note)) setClosing(false) }} />
    </Dialog>
  )
}

// A task contact's Task card (2026-10-13), in place of its message bubble:
// the task's live fields and full history, with Edit.
export function TaskContactCard({ taskId, onSaved = null }) {
  const details = useTaskDetails(taskId, { onSaved })
  const { task, editing, setEditing, saving, error, startEdit, save, values } = details
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="task-card">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" data-testid="task-card-title">{task ? `${task.displayId} · ${taskTypeLabel(task.type)}` : 'Task'}</Typography>
        <Box sx={{ flex: 1 }} />
        {editing ? (
          <>
            <Button size="small" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button size="small" variant="contained" onClick={save} disabled={saving || !taskFormReady(task.type, values)} data-testid="task-card-save">Save</Button>
          </>
        ) : (
          <Button size="small" variant="outlined" onClick={startEdit} disabled={!task || task.status === 'closed'} data-testid="task-card-edit">Edit</Button>
        )}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <TaskDetailsBody details={details} />
    </Paper>
  )
}

function Field({ label, children }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant="body2" component="div">{children}</Typography>
    </Box>
  )
}

// The filter controls use the Queue filter panel's sizes (App.jsx), on the
// theme's font (2026-10-13).
const FILTER_SX = {
  '& .MuiInputLabel-root': { fontSize: '0.75rem' },
  '& .MuiSelect-select': { py: '4px', fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: '4px', fontSize: '0.75rem' },
}

function PresetSelect({ label, value, presets, onChange, testId }) {
  return (
    <FormControl size="small" sx={{ minWidth: 170, ...FILTER_SX }}>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        displayEmpty label={label} value={value} onChange={(e) => onChange(e.target.value)}
        renderValue={(v) => presets.find((p) => p.key === v)?.label ?? 'All'}
        SelectDisplayProps={{ 'data-testid': testId }}
      >
        <MenuItem value="" dense data-testid={`${testId}-all`}>All</MenuItem>
        {presets.map((p) => <MenuItem key={p.key} value={p.key} dense data-testid={`${testId}-${p.key}`}>{p.label}</MenuItem>)}
      </Select>
    </FormControl>
  )
}

export const TASK_COLUMNS = ['ID', 'Type', 'Order Number', 'Description', 'Action Needed By Date', 'Created On', 'Created By', 'Status']
// The closed view (2026-10-13) adds when and by whom.
export const CLOSED_TASK_COLUMNS = [...TASK_COLUMNS, 'Closed On', 'Closed By']
// The headers that sort, by the column key nextTaskSort() takes.
const SORTABLE = { 'Action Needed By Date': 'action', 'Created On': 'created' }

// onOpenContact(conversationId): opens a task's contact (2026-10-13).
export default function TasksView({ now = null, onOpenContact = null }) {
  const [filters, setFilters] = useState(() => readTaskFilters())
  // Not remembered: a reload starts on the default.
  const [sort, setSort] = useState(DEFAULT_TASK_SORT)
  const [data, setData] = useState({ tasks: [], creators: [], orderLinks: {} })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [creating, setCreating] = useState(false)
  const [createdTask, setCreatedTask] = useState(null)
  // /tasks?task=1001 opens that task's details (2026-10-15: the link on a
  // contact's Activity Log line).
  const [openId, setOpenId] = useState(() => taskIdFromLocation())
  const query = useMemo(() => taskListQuery(filters, sort), [filters, sort])

  const load = useCallback(() => {
    setLoading(true)
    fetchTasks(query)
      .then((d) => { setData({ tasks: d.tasks ?? [], creators: d.creators ?? [], orderLinks: d.orderLinks ?? {} }); setError(null) })
      .catch(() => setError('Could not load tasks.'))
      .finally(() => setLoading(false))
  }, [query])
  useEffect(() => { load() }, [load])

  const updateFilters = (next) => { setFilters(next); writeTaskFilters(next) }
  const setFilter = (k, v) => updateFilters({ ...filters, [k]: v })
  const clearFilters = () => { updateFilters({ ...EMPTY_TASK_FILTERS, createdBy: [] }); setSort(DEFAULT_TASK_SORT) }
  const nowDate = now ?? new Date()

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }} data-testid="tasks-view">
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5 }}>
        <Typography variant="h6">Tasks</Typography>
        <Button variant="contained" size="small" onClick={() => setCreating(true)} data-testid="task-new">New task</Button>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }} data-testid="task-filters">
        <FormControl size="small" sx={{ minWidth: 180, ...FILTER_SX }}>
          <InputLabel shrink>Created By</InputLabel>
          <Select
            multiple displayEmpty label="Created By" value={filters.createdBy}
            onChange={(e) => setFilter('createdBy', typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            renderValue={(sel) => (sel.length ? sel.map((oid) => data.creators.find((c) => c.oid === oid)?.name ?? oid).join(', ') : 'Everyone')}
            SelectDisplayProps={{ 'data-testid': 'task-filter-created-by' }}
          >
            {data.creators.map((c) => (
              <MenuItem key={c.oid} value={c.oid} dense>
                <Checkbox size="small" checked={filters.createdBy.includes(c.oid)} />
                <ListItemText primary={c.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <PresetSelect label="Created On" value={filters.created} presets={TASK_CREATED_PRESETS} onChange={(v) => setFilter('created', v)} testId="task-filter-created" />
        <PresetSelect label="Action Needed By" value={filters.action} presets={TASK_ACTION_PRESETS} onChange={(v) => setFilter('action', v)} testId="task-filter-action" />
        <TextField
          size="small" label="Search" placeholder="Order number, description or ID (T1001)" value={filters.q}
          onChange={(e) => setFilter('q', e.target.value)} InputLabelProps={{ shrink: true }}
          sx={{ flex: 1, minWidth: 320, ...FILTER_SX }} inputProps={{ 'data-testid': 'task-filter-q' }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={filters.closed} onChange={(e) => setFilter('closed', e.target.checked)} inputProps={{ 'data-testid': 'task-show-closed' }} />}
          label={<Typography variant="body2" sx={{ fontSize: '0.75rem' }}>Show closed tasks</Typography>}
        />
        {(hasTaskFilters(filters) || sort !== DEFAULT_TASK_SORT) && (
          <Link component="button" type="button" variant="body2" onClick={clearFilters} data-testid="task-filters-clear">Clear filters</Link>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="task-count">{loading ? 'Loading…' : `${data.tasks.length} task${data.tasks.length === 1 ? '' : 's'}`}</Typography>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Paper variant="outlined">
        <Table size="small" data-testid="task-table">
          <TableHead>
            <TableRow>
              {(filters.closed ? CLOSED_TASK_COLUMNS : TASK_COLUMNS).map((c) => {
                const column = SORTABLE[c]
                if (!column) return <TableCell key={c}>{c}</TableCell>
                const direction = taskSortDirection(sort, column)
                return (
                  <TableCell key={c} sortDirection={direction ?? false}>
                    <TableSortLabel active={Boolean(direction)} direction={direction ?? 'asc'} onClick={() => setSort(nextTaskSort(sort, column))} data-testid={`task-sort-${column}`}>
                      {c}
                    </TableSortLabel>
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.tasks.map((t) => {
              const due = isTaskDue(t, nowDate)
              const cell = { verticalAlign: 'top', ...(due ? { color: 'error.main' } : {}) }
              return (
                <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenId(t.id)} data-testid="task-row" data-due={due ? 'true' : undefined}>
                  <TableCell sx={cell} data-testid="task-id">{t.displayId}</TableCell>
                  <TableCell sx={cell}>{taskTypeLabel(t.type)}</TableCell>
                  <TableCell sx={{ ...cell, maxWidth: 280, overflowWrap: 'anywhere' }} data-testid="task-order-details"><OrderDetailsText text={t.orderDetails} orderLinks={data.orderLinks} /></TableCell>
                  <TableCell sx={{ ...cell, maxWidth: 360, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{t.description}</TableCell>
                  <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }} data-testid="task-action-needed-by">{formatTaskActionNeededBy(t)}</TableCell>
                  <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>{formatTaskTimestamp(t.createdAt)}</TableCell>
                  <TableCell sx={cell}>{t.createdByName ?? ''}</TableCell>
                  <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }} data-testid="task-status-cell">{TASK_STATUS_LABELS[t.status] ?? t.status ?? ''}</TableCell>
                  {filters.closed && <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }} data-testid="task-closed-on">{formatTaskTimestamp(t.closedAt)}</TableCell>}
                  {filters.closed && <TableCell sx={cell} data-testid="task-closed-by">{t.closedByName ?? ''}</TableCell>}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {!loading && !data.tasks.length && <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No tasks.</Typography>}
      </Paper>

      <CreateTaskDialog open={creating} onClose={() => setCreating(false)} onCreated={(task) => { setCreating(false); setCreatedTask(task); load() }} />
      <TaskCreatedSnackbar task={createdTask} onClose={() => setCreatedTask(null)} />
      <TaskDetailsDialog taskId={openId} onClose={() => setOpenId(null)} onSaved={load} onOpenContact={onOpenContact ? (id) => { setOpenId(null); onOpenContact(id) } : null} />
    </Box>
  )
}
